import type { Lang, LorsCopy } from "./translations";

export type Nov10Headline = { headline: string; subhead: string };

export const nov10Headlines: Nov10Headline[] = [
  {
    headline: "A will alone won’t keep your home out of probate. Here’s what will.",
    subhead:
      "Join attorney Ruby Steinbrecher on November 10th for a one-hour session on wills, trusts, and California probate.",
  },
  {
    headline: "Your kids get the house without a year of court dates and paperwork.",
    subhead:
      "Learn how California families use trusts to pass the home on without probate court. Free workshop, November 10th.",
  },
  {
    headline: "Name your trust after your dog. Learn the rest in one free hour.",
    subhead:
      "Reserve your spot for the free estate planning workshop on November 10th at Finley Community Center.",
  },
  {
    headline: "Probate can tie up your home for over a year. One hour can change that.",
    subhead:
      "We’ll break down the probate process and how a funded trust protects your home. November 10th.",
  },
  {
    headline: "Inheriting your home should feel like a gift, not a court case.",
    subhead:
      "See how a living trust helps your family inherit without the courthouse. Free workshop on November 10th.",
  },
  {
    headline: "Most people guess wrong. Find out which one actually keeps your family out of court.",
    subhead:
      "Wills, trusts, or both? Get clear answers in one hour on November 10th.",
  },
  {
    headline: "The house your family grew up in deserves a plan, not a court hearing.",
    subhead:
      "Join attorney Ruby Steinbrecher on November 10th and give your family a plan instead of probate.",
  },
  {
    headline: "Ask the questions you’ve been putting off and get straight answers in one hour.",
    subhead:
      "Bring your questions to the free workshop on November 10th. No pressure, just clear information.",
  },
  {
    headline: "The conversation your family will be glad you had, while you’re here to have it.",
    subhead:
      "A practical, no-pressure estate planning session on November 10th with attorney Ruby Steinbrecher.",
  },
  {
    headline: "Even with a will, your home can still end up in court. Here’s how to keep it out.",
    subhead:
      "Find out why a will alone may still send your family through probate. Free workshop on November 10th.",
  },
  {
    headline: "You decided every detail of that house. You should decide what happens to it next.",
    subhead:
      "Your plan is yours to make. Learn your options at the free workshop on November 10th.",
  },
  {
    headline: "Spare your kids a year of court, fees, and paperwork. It takes one hour to start.",
    subhead:
      "One hour now could save your family a year in court. Join us on November 10th.",
  },
  {
    headline: "Skip the courtroom reunion and give your family a plan instead.",
    subhead:
      "Learn how California families use trusts to keep their homes out of probate court. November 10th.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Workshop",
  headline: nov10Headlines[0].headline,
  headlineBreakBefore: "",
  subhead: nov10Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What Most Families Don't Realize Until It's Too Late",
  learnItems: [
    "How wills, living trusts, and powers of attorney each work in California",
    "How a properly funded living trust can help a family pass assets without probate court",
    "What California's probate process generally looks like — timelines, court involvement, and typical costs",
    "Practical first steps for putting an estate plan in place, and the questions to ask before you do",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Tue, November 10th", long: "Tuesday, November 10th" },
  formTime: "4:00–5:00 PM",
  formVenueLine1: "Finley Community Center",
  formVenueLine2: "2060 W College Ave, Santa Rosa, CA 95401",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited seating available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "⭐ Trusted by local families | 81 Five-Star Reviews",
  getDirections: "Get Directions",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey\'s bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000\'s.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Secure Your Family's Future?",
  registrationSub: "Free to attend. Limited seating available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make the right decision for your family.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentNov10 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const NOV10_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=Finley+Community+Center,+2060+West+College+Ave,+Santa+Rosa,+CA+95401";

export const getNov10Copy = (hParam: string | null): LorsCopy => {
  const n = Number(hParam);
  const idx = Number.isInteger(n) && n >= 1 && n <= nov10Headlines.length ? n - 1 : 0;
  const pick = nov10Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
